import OpenAI from 'openai';
import type { ChatCompletionMessageParam } from 'openai/resources/chat/completions';
import { getChatbotConfigSection } from '../utils/chatbotConfigAccess';
import { getSecurityPrompt } from '../utils/securityPrompt';
import { supabase } from '../lib/supabase';

// Define interface for AI settings
interface AISettings {
  apiKey: string;
  prompts: {
    generalPrompt: string;
    attitudePrompt: string;
  };
  knowledgeBase: Array<{
    id?: string;
    type?: string;
    content?: string;
    url?: string;
  }>;
  objectives: string[];
  temperature: number;
  fineTunedModels?: {
    active?: string;       // L'ID du modèle fine-tuné actif
    available?: string[];  // Liste des modèles fine-tunés disponibles pour ce chatbot
    status?: string;       // État du dernier job de fine-tuning
    lastTrainingDate?: string; // Date du dernier fine-tuning
    jobId?: string;       // ID du dernier job de fine-tuning
  };
}

const DEFAULT_API_KEY = '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';

const DEFAULT_PROMPTS = {
  generalPrompt: 'You are a helpful customer service assistant.',
  attitudePrompt: 'Be concise, friendly, and accurate.'
};

const DEFAULT_TEMPERATURE = 0.7;

async function getMiniAppsData(chatbotPublicId: string): Promise<string> {
  let knowledgeBase = '';

  // Get products data
  const { data: products } = await supabase
    .from('products')
    .select('*')
    .eq('chatbot_public_id', chatbotPublicId);

  if (products && products.length > 0) {
    knowledgeBase += '\nProduits disponibles:\n';
    products.forEach(product => {
      knowledgeBase += `- ${product.name}\n`;
      if (product.description) knowledgeBase += `  Description: ${product.description}\n`;
      knowledgeBase += `  Prix: ${product.price}${product.currency}\n`;
      if (product.discount) knowledgeBase += `  Réduction: ${product.discount}%\n`;
      if (product.stock !== undefined) knowledgeBase += `  Stock disponible: ${product.stock}\n`;
      if (product.category) knowledgeBase += `  Catégorie: ${product.category}\n`;
      if (product.tags?.length) knowledgeBase += `  Tags: ${product.tags.join(', ')}\n`;
      if (product.metadata?.redirect_url) knowledgeBase += `  Plus d'informations: ${product.metadata.redirect_url}\n`;
      if (product.image_url) knowledgeBase += `  Image: ${product.image_url}\n`;
      knowledgeBase += '\n';
    });
  }

  // Get services data
  const { data: services } = await supabase
    .from('services')
    .select('*')
    .eq('chatbot_public_id', chatbotPublicId);

  if (services && services.length > 0) {
    knowledgeBase += '\nServices proposés:\n';
    services.forEach(service => {
      knowledgeBase += `- ${service.title}\n`;
      if (service.subtitle) knowledgeBase += `  ${service.subtitle}\n`;
      knowledgeBase += `  ${service.content}\n`;
      if (service.button_url) knowledgeBase += `  En savoir plus: ${service.button_url}\n`;
      if (service.image_url) knowledgeBase += `  Image: ${service.image_url}\n`;
      knowledgeBase += '\n';
    });
  }

  // Get real estate properties
  const { data: properties } = await supabase
    .from('realestate_properties')
    .select('*')
    .eq('chatbot_public_id', chatbotPublicId);

  if (properties && properties.length > 0) {
    knowledgeBase += '\nBiens immobiliers disponibles:\n';
    properties.forEach(property => {
      knowledgeBase += `- ${property.title}\n`;
      if (property.description) knowledgeBase += `  Description: ${property.description}\n`;
      knowledgeBase += `  Type de transaction: ${property.transaction_type === 'sale' ? 'Vente' : 'Location'}\n`;
      knowledgeBase += `  Prix: ${property.price}${property.currency}\n`;
      if (property.surface) knowledgeBase += `  Surface: ${property.surface}${property.size_unit}\n`;
      if (property.rooms) knowledgeBase += `  Nombre de pièces: ${property.rooms}\n`;
      if (property.bedrooms) knowledgeBase += `  Chambres: ${property.bedrooms}\n`;
      if (property.bathrooms) knowledgeBase += `  Salles de bain: ${property.bathrooms}\n`;
      if (property.location) knowledgeBase += `  Localisation: ${property.location}\n`;
      if (property.address) knowledgeBase += `  Adresse: ${property.address}\n`;
      if (property.city) knowledgeBase += `  Ville: ${property.city}\n`;
      if (property.features?.length) knowledgeBase += `  Caractéristiques: ${property.features.join(', ')}\n`;
      if (property.target_roi) knowledgeBase += `  ROI cible: ${property.target_roi}%\n`;
      if (property.estimated_resale) knowledgeBase += `  Revente estimée: ${property.estimated_resale}\n`;
      if (property.metadata?.external_url) knowledgeBase += `  Plus d'informations: ${property.metadata.external_url}\n`;
      if (property.images?.length) knowledgeBase += `  Images: ${property.images.join(', ')}\n`;
      knowledgeBase += '\n';
    });
  }

  // Get Calendly configuration
  const { data: calendlyConfig } = await supabase
    .from('chatbot_configs')
    .select('calendly_config')
    .eq('chatbot_public_id', chatbotPublicId)
    .single();

  if (calendlyConfig?.calendly_config?.url) {
    knowledgeBase += '\nPrise de rendez-vous:\n';
    knowledgeBase += `- Lien Calendly disponible pour la prise de rendez-vous: ${calendlyConfig.calendly_config.url}\n`;
  }

  return knowledgeBase;
}

async function createOpenAIClient(chatbotPublicId: string) {
  const aiSettings = await getChatbotConfigSection<AISettings>('ai_settings', chatbotPublicId);
  
  // Use the configured API key if it exists and is not empty, otherwise use the default key
  const apiKey = aiSettings?.apiKey && aiSettings.apiKey.trim() !== '' ? aiSettings.apiKey : DEFAULT_API_KEY;

  return new OpenAI({
    apiKey,
    dangerouslyAllowBrowser: true
  });
}

export async function generateChatResponse(message: string, context: string, chatbotPublicId: string) {
  try {
    // Get AI settings using centralized config access
    const settings = await getChatbotConfigSection<AISettings>('ai_settings', chatbotPublicId);
    
    // Create OpenAI client with appropriate API key
    const openai = await createOpenAIClient(chatbotPublicId);
    
    // Use default prompts if settings or prompts are missing
    const prompts = settings?.prompts || DEFAULT_PROMPTS;
    const systemPrompt = `${prompts.generalPrompt}\n${prompts.attitudePrompt}`;
    
    // Use empty array if objectives are missing
    const objectives = settings?.objectives || [];
    const objectivesText = objectives.length > 0 ? objectives.join('\n') : '';
    
    // Build the knowledge base
    const knowledgeBase = [
      // Include existing knowledge base
      ...(settings?.knowledgeBase || [])
        .map(item => item.content || '')
        .filter(Boolean),
      
      // Add mini-apps data
      await getMiniAppsData(chatbotPublicId)
    ].join('\n\n');

    const messages: ChatCompletionMessageParam[] = [
      // Security prompt first (non-modifiable and not visible)
      {
        role: "system",
        content: getSecurityPrompt()
      },
      // Company-specific prompts
      { 
        role: "system", 
        content: systemPrompt 
      }
    ];

    // Add objectives if they exist
    if (objectivesText) {
      messages.push({ 
        role: "system", 
        content: `Objectifs spécifiques:\n${objectivesText}` 
      });
    }

    // Add knowledge base if it exists
    if (knowledgeBase) {
      messages.push({
        role: "system",
        content: `Base de connaissances:\n${knowledgeBase}`
      });
    }

    // Add conversation context if it exists
    if (context) {
      messages.push({
        role: "system",
        content: `Contexte de la conversation:\n${context}`
      });
    }

    // Add user message
    messages.push({
      role: "user",
      content: message
    });

    // Déterminer quel modèle utiliser (utiliser le modèle fine-tuné si disponible)
    const model = settings?.fineTunedModels?.active && 
                  settings.fineTunedModels.active.trim() !== '' 
                  ? settings.fineTunedModels.active 
                  : "gpt-4.1-mini"; // modèle par défaut

    const response = await openai.chat.completions.create({
      model: model,
      messages,
      stream: true,
      temperature: settings?.temperature || DEFAULT_TEMPERATURE,
      max_tokens: 500,
      presence_penalty: 0.6,
      frequency_penalty: 0.5
    });

    return response;
  } catch (error) {
    console.error('Error generating chat response:', error);
    throw error;
  }
}
